import { useEffect, useRef, useState, type ReactNode } from 'react';
import { parseCookies } from 'nookies';
import ReaderPageImage from './ReaderPageImage';
import { fetchChapterPagesArray, pageEntryToSrc } from './chapter-cdn-pages';
import { api } from '@/utils/api';
import ChapterInteractions from './ChapterInteractions';

interface VerticalReaderProps {
    capitulo: any;
    imageZoom?: number;
    imageBrightness?: number;
    infiniteScroll?: boolean;
    /** Conteúdo imediatamente antes das reações/comentários (ex.: navegação cap. anterior/seguinte). */
    beforeInteractions?: ReactNode;
}

const markedSession = new Set<string>();

export default function VerticalReader({
    capitulo,
    imageZoom = 100,
    imageBrightness = 100,
    infiniteScroll = false,
    beforeInteractions = null,
}: VerticalReaderProps) {
    const [paginas, setPaginas] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const capId = String(capitulo?.cap_id ?? capitulo?.id ?? '');
    const capituloRef = useRef(capitulo);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const markAttemptedRef = useRef(false);
    capituloRef.current = capitulo;

    async function markChapterAsRead() {
        if (markAttemptedRef.current) return;
        markAttemptedRef.current = true;

        if (!capId || markedSession.has(capId)) return;

        const token = parseCookies()['token'];
        if (!token) return;

        const chapter = capituloRef.current;
        const cap_id = Number(chapter?.cap_id ?? chapter?.id);
        const obr_id = Number(chapter?.obra?.id ?? chapter?.obra?.obr_id ?? chapter?.obr_id);
        if (!Number.isInteger(cap_id) || cap_id < 1 || !Number.isInteger(obr_id) || obr_id < 1) return;

        try {
            await api.post('readings/mark', { obr_id, cap_id });
            markedSession.add(capId);
        } catch {
            // silencioso: falha de auth/rede não deve quebrar a leitura
        }
    }

    useEffect(() => {
        if (!capId) {
            setPaginas([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const list = await fetchChapterPagesArray(capituloRef.current);
                if (!cancelled) setPaginas(list);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar páginas');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 100);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [capId]);

    useEffect(() => {
        markAttemptedRef.current = markedSession.has(capId);
    }, [capId]);

    useEffect(() => {
        if (!capId || paginas.length === 0) return;
        if (markedSession.has(capId)) return;

        const root = rootRef.current;
        if (!root) return;

        const threshold = Math.max(1, Math.ceil(paginas.length / 2));
        const seenIndexes = new Set<number>();
        const targets = root.querySelectorAll<HTMLElement>('[data-page-index]');
        if (targets.length === 0) return;

        let cancelled = false;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    const indexRaw = (entry.target as HTMLElement).dataset.pageIndex;
                    const index = Number(indexRaw);
                    if (!Number.isInteger(index)) continue;
                    seenIndexes.add(index);
                }

                if (!cancelled && seenIndexes.size >= threshold) {
                    cancelled = true;
                    observer.disconnect();
                    void markChapterAsRead();
                }
            },
            { root: null, threshold: 0.45 }
        );

        targets.forEach((el) => observer.observe(el));
        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [capId, paginas.length]);

    if (loading) {
        return (
            <div className="w-full py-24 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full py-16 px-4 text-center text-red-400/90 text-sm">
                {error}
            </div>
        );
    }

    if (paginas.length === 0) {
        return (
            <div className="w-full py-16 px-4 text-center text-white/50 text-sm">
                Nenhuma página encontrada.
            </div>
        );
    }

    return (
        <div ref={rootRef} className="w-full flex flex-col gap-0">
            {paginas.map((pagina, index) => {
                const url = pageEntryToSrc(pagina);
                if (!url) {
                    return (
                        <div
                            key={`skip-${index}`}
                            data-page-index={index}
                            className="w-full min-h-[120px] flex items-center justify-center text-white/40 text-xs"
                        >
                            Página {index + 1} inválida
                        </div>
                    );
                }
                return (
                    <div key={`${url}-${index}`} data-page-index={index}>
                        <ReaderPageImage
                            src={url}
                            alt={`Página ${index + 1}`}
                            fetchPriority={index < 3 ? 'high' : 'auto'}
                            zoomPercent={imageZoom}
                            brightnessPercent={imageBrightness}
                        />
                    </div>
                );
            })}
            {beforeInteractions}
            <ChapterInteractions capitulo={capitulo} infiniteScroll={infiniteScroll} />
        </div>
    );
}
